import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/AuthContext';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';

/**
 * Hook to automatically log out users after a period of inactivity
 * @param {number} timeoutMinutes - Minutes of inactivity before logout (default: 30)
 * @param {number} warningMinutes - Minutes before timeout to show warning (default: 5)
 */
export function useInactivityTimeout(timeoutMinutes = 30, warningMinutes = 5) {
  const { isAuthenticated, signOut } = useAuth();
  const queryClient = useQueryClient();
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);
  const warningShownRef = useRef(false);

  const timeoutMs = timeoutMinutes * 60 * 1000;
  const warningMs = (timeoutMinutes - warningMinutes) * 60 * 1000;

  const handleLogout = useCallback(async () => {
    logger.debug('Session timeout - logging out due to inactivity');
    toast.error('Sesja wygasła z powodu braku aktywności. Zaloguj się ponownie.', {
      duration: 5000,
    });
    try {
      await signOut(queryClient);
      window.location.href = '/login';
    } catch (error) {
      logger.error('Logout error:', error);
      window.location.href = '/login';
    }
  }, [signOut, queryClient]);

  const showWarning = useCallback(() => {
    if (!warningShownRef.current) {
      warningShownRef.current = true;
      toast.warning(
        `Twoja sesja wygaśnie za ${warningMinutes} ${warningMinutes === 1 ? 'minutę' : 'minuty'} z powodu braku aktywności. Kliknij gdziekolwiek, aby pozostać zalogowanym.`,
        {
          duration: warningMinutes * 60 * 1000,
          id: 'session-warning',
        }
      );
    }
  }, [warningMinutes]);

  const resetTimers = useCallback(() => {
    // Clear existing timers
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);

    // Reset warning state
    warningShownRef.current = false;
    toast.dismiss('session-warning');

    // Only set timers if authenticated
    if (!isAuthenticated) return;

    // Set warning timer
    warningRef.current = setTimeout(showWarning, warningMs);

    // Set logout timer
    timeoutRef.current = setTimeout(handleLogout, timeoutMs);
  }, [isAuthenticated, timeoutMs, warningMs, handleLogout, showWarning]);

  useEffect(() => {
    if (!isAuthenticated) {
      // Clear timers if not authenticated
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
      return;
    }

    // Activity events to track
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ];

    // Throttle reset to avoid excessive calls
    let lastReset = Date.now();
    const throttleMs = 1000; // Only reset once per second max

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastReset >= throttleMs) {
        lastReset = now;
        resetTimers();
      }
    };

    // Add event listeners
    activityEvents.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Initial timer setup
    resetTimers();

    // Cleanup
    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
    };
  }, [isAuthenticated, resetTimers]);

  return { resetTimers };
}
